import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { campaignService } from '../services/campaignService';
import { getImageUrl } from '../services/imageService';
import Loading from '../components/common/Loading';
import './Campaigns.css';

const PLACEMENT_LABELS = {
  hero: 'Hero slider',
  strip: 'Promo strip',
  wide: 'Wide banner',
  section: 'Product section',
};

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : null;

/**
 * A campaign is only live when it is active AND inside its date window, so the
 * admin shows the effective state rather than just the is_active flag.
 */
const getLiveState = (campaign) => {
  if (!campaign.is_active) return { label: 'Inactive', className: 'inactive' };

  const now = Date.now();
  if (campaign.start_date && new Date(campaign.start_date).getTime() > now) {
    return { label: 'Scheduled', className: 'scheduled' };
  }
  if (campaign.end_date && new Date(campaign.end_date).getTime() < now) {
    return { label: 'Expired', className: 'expired' };
  }
  return { label: 'Live', className: 'live' };
};

const Campaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await campaignService.getAllCampaigns({
        page: pagination.page,
        limit: pagination.limit,
      });

      setCampaigns(data.campaigns || []);
      setPagination((prev) => ({
        ...prev,
        ...data.pagination,
      }));
    } catch (err) {
      setError(err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleDelete = async (campaignId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      await campaignService.deleteCampaign(campaignId);
      loadCampaigns();
    } catch (err) {
      alert('Failed to delete campaign: ' + (err.message || 'Unknown error'));
    }
  };

  if (loading) {
    return <Loading message="Loading campaigns..." />;
  }

  return (
    <div className="campaigns-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Campaigns</h1>
          <p className="page-subtitle">
            Promotions shown on the home page. Each one opens its own product list.
          </p>
        </div>
        <Link to="/campaigns/create" className="btn-primary">
          Create Campaign
        </Link>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="campaigns-content">
        {campaigns.length === 0 ? (
          <div className="empty-state">
            <p>No campaigns yet.</p>
            <Link to="/campaigns/create" className="btn-primary">
              Create Your First Campaign
            </Link>
          </div>
        ) : (
          <>
            <div className="campaigns-table-container">
              <table className="campaigns-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Title</th>
                    <th>Placement</th>
                    <th>Schedule</th>
                    <th>Products</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => {
                    const imageUrl = campaign.image_url
                      ? getImageUrl(campaign.image_url)
                      : null;
                    const state = getLiveState(campaign);
                    const from = formatDate(campaign.start_date);
                    const to = formatDate(campaign.end_date);

                    return (
                      <tr key={campaign.id}>
                        <td>
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={campaign.title}
                              className="campaign-thumbnail"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="no-image">No Image</div>
                          )}
                        </td>
                        <td>
                          <div className="campaign-name">{campaign.title}</div>
                          <code className="campaign-slug">/campaigns/{campaign.slug}</code>
                        </td>
                        <td>
                          <span className="placement-badge">
                            {PLACEMENT_LABELS[campaign.placement] || campaign.placement}
                          </span>
                        </td>
                        <td>
                          {from || to ? (
                            <span className="campaign-schedule">
                              {from || 'Always'} &rarr; {to || 'No end'}
                            </span>
                          ) : (
                            <span className="text-muted">Always on</span>
                          )}
                        </td>
                        <td>{campaign.product_count ?? 0}</td>
                        <td>{campaign.display_order}</td>
                        <td>
                          <span className={`status-badge status-${state.className}`}>
                            {state.label}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => navigate(`/campaigns/edit/${campaign.id}`)}
                              className="btn-edit"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(campaign.id, campaign.title)}
                              className="btn-delete"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                  disabled={pagination.page === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </span>
                <button
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                  }
                  disabled={pagination.page >= pagination.pages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Campaigns;
